/**
 * White-label theme defaults.
 *
 * Brand name / logo / title are overridden by `APP_*` env vars
 * (`src/config/appEnv.ts`). Colors, fonts, and buttons stay here.
 *
 * Applied at startup by `applyTheme()` in `main.tsx`.
 */

import { appEnv } from "@/config/appEnv";

const defaultThemeConfig = {
  brand: {
    name: "BanknoteID",
    appTitle: "BanknoteID Expert Portal",
    logoSrc: "/banknote-logo.png",
    logoAlt: "BanknoteID",
    faviconSrc: "/favicon.svg",
    reportName: "BanknoteID",
    portalLabels: {
      expert: "Expert Portal",
      client: "Client Portal",
    },
    panelSubtitle: "Expert panel",
  },

  fonts: {
    /** CSS font-family for body UI */
    sans: '"Inter", ui-sans-serif, system-ui, sans-serif',
    /** Optional display font (reports / headings) */
    display: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    /** Google Fonts stylesheet — change family query when swapping fonts */
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap",
  },

  colors: {
    canvas: "#F3F5F7",
    surface: "#ffffff",

    primary: "#275F4F",
    primaryHover: "#1E513D",
    primaryActive: "#1A3A2E",
    primarySoft: "#EEF6F1",

    secondary: "#C4A15D",
    secondaryHover: "#B08D4A",
    secondaryActive: "#8A6E38",
    secondarySoft: "#F8F1E3",

    text: "#1E1E1F",
    textMuted: "#8A8F8C",
    border: "#E1E4E2",
    inputBg: "#F9FAFB",
    inputBorder: "#E6E9EB",

    logoBg: "#001E0A",
    logoAccent: "#F0A924",
    successRing: "#EEF6F1",
    successCheck: "#275F4F",

    expertSidebar: "#275F4F",
    expertSidebarHeader: "#1F4C3F",
    expertSidebarActive: "#275F4F",
    expertSidebarForeground: "#ffffff",
    expertSidebarMuted: "rgba(255, 255, 255, 0.7)",
    /** Figma `--Card-Selected` — highlighted dashboard stat card (Active cases). */
    expertCardSelected: "#5B8B7B",
    expertDashboardCanvas: "#F3F5F7",
    expertNavBadge: "#FF6900",
    expertAvailable: "#30ED9D",
    expertStatusActive: "#30ED9D",
    expertStatusInactive: "#F0A924",

    expertActionGreen: "#275F4F",
    expertActionGreenHover: "#1E513D",
    expertActionGreenSoft: "#EEF6F1",
    expertActionGreenText: "#145344",
    expertActionGreenRing: "rgba(39, 95, 79, 0.22)",

    expertActionBlue: "#2563eb",
    expertActionBlueHover: "#1d4ed8",
    expertActionBlueSoft: "#eff6ff",
    expertActionBlueText: "#1e40af",
    expertActionBlueRing: "rgba(37, 99, 235, 0.25)",

    expertActionAmber: "#F0A924",
    expertActionAmberHover: "#D4921A",
    expertActionAmberSoft: "#FEF6E4",
    expertActionAmberText: "#854d0e",
    expertActionAmberRing: "rgba(240, 169, 36, 0.45)",

    expertActionOrange: "#C4A15D",
    expertActionOrangeHover: "#B08D4A",

    /** Queue “In queue” pill — Figma light blue, not the pine badge. */
    expertBadgeNeutral: "#EDF6FF",
    expertBadgeNeutralText: "#50A4FE",
    expertBadgeNeutralRing: "rgba(80, 164, 254, 0.25)",

    /** Queue request card accents from the Experts Feature frame. */
    expertQueueAccentBlue: "#50A4FE",
    expertQueueAccentBlueHover: "#3B93F0",
    expertQueueAccentGreen: "#06DF74",
    expertQueueAccentGreenHover: "#05C868",
    expertQueueCardBorder: "#D9D9D9",
    expertQueueDivider: "#D9D9D9",
    expertQueueProgressBg: "#DCFCE7",
    expertQueueProgressText: "#166534",

    expertStatusDraftBg: "#F3F5F7",
    expertStatusDraftText: "#4b5563",
    expertStatusNewBg: "#FEF6E4",
    expertStatusNewText: "#854d0e",
    expertStatusDoneBg: "#EEF6F1",
    expertStatusDoneText: "#145344",
    expertStatusExpiredBg: "#fff1eb",
    expertStatusExpiredText: "#c2410c",

    expertError: "#dc2626",
    expertErrorSoft: "#fef2f2",

    expertDraftBanner: "#EEF6F1",
    expertDraftBannerBorder: "#B7D7CC",
    expertDraftBannerText: "#145344",
  },

  buttons: {
    sizes: {
      sm: {
        height: "1.75rem",
        paddingX: "1.75rem",
        fontSize: "0.75rem",
        fontWeight: "600",
        radius: "0.5rem",
        minWidth: "",
      },
      md: {
        height: "2.5rem",
        paddingX: "1rem",
        fontSize: "0.875rem",
        fontWeight: "600",
        radius: "0.5rem",
        minWidth: "8.5rem",
      },
      lg: {
        height: "auto",
        paddingX: "1rem",
        paddingY: "0.75rem",
        fontSize: "0.875rem",
        fontWeight: "600",
        radius: "9999px",
        minWidth: "",
      },
    },
    variants: {
      primary: {
        bg: "bg-primary",
        hover: "hover:bg-primary-hover",
        active: "active:bg-primary-active",
        text: "text-white",
        border: "",
      },
      secondary: {
        bg: "bg-secondary",
        hover: "hover:bg-secondary-hover",
        active: "active:bg-secondary-active",
        text: "text-white",
        border: "",
      },
      outline: {
        bg: "bg-white",
        hover: "hover:bg-primary/[0.04]",
        active: "",
        text: "text-primary",
        border: "border border-primary",
      },
      ghost: {
        bg: "bg-transparent",
        hover: "hover:bg-primary-soft",
        active: "",
        text: "text-primary",
        border: "",
      },
      success: {
        bg: "bg-expert-action-green",
        hover: "hover:bg-expert-action-green-hover",
        active: "",
        text: "text-white",
        border: "",
      },
      info: {
        bg: "bg-expert-action-blue",
        hover: "hover:bg-expert-action-blue-hover",
        active: "",
        text: "text-white",
        border: "",
      },
    },
  },

  icons: {
    nav: {
      sizePx: 18,
      strokeWidth: 1.75,
      queueSrc: "/expert-nav-queue.png",
    },
    logo: {
      sizePx: 40,
      radius: "0.5rem",
    },
  },

  /** Evaluation / PDF report copy (brand colors still come from `colors`) */
  report: {
    subtitle: "Banknote Evaluation",
    title: "Evaluation Report",
    layoutVersion: "v1" as "v1" | "v2",
  },
} as const;

export const themeConfig = {
  ...defaultThemeConfig,
  brand: {
    ...defaultThemeConfig.brand,
    name: appEnv.name || defaultThemeConfig.brand.name,
    appTitle: appEnv.title || defaultThemeConfig.brand.appTitle,
    logoSrc: appEnv.logoUrl || defaultThemeConfig.brand.logoSrc,
    logoAlt: appEnv.name || defaultThemeConfig.brand.logoAlt,
    faviconSrc: appEnv.faviconUrl || defaultThemeConfig.brand.faviconSrc,
    reportName: appEnv.reportName || defaultThemeConfig.brand.reportName,
  },
};

export type ThemeConfig = typeof defaultThemeConfig;
export type ButtonSize = keyof typeof defaultThemeConfig.buttons.sizes;
export type ButtonVariant = keyof typeof defaultThemeConfig.buttons.variants;
